import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Calendar as CalIcon, Image as ImageIcon, Check, ArrowLeft, ArrowRight, X, CreditCard, Smartphone, Wallet, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";

const TIME_SLOTS = ["08:00 AM", "10:00 AM", "12:00 PM", "02:00 PM", "04:00 PM"];

const PAYMENTS = [
  { id: "stripe", label: "Credit Card (Stripe)", icon: CreditCard },
  { id: "applepay", label: "Apple Pay", icon: Smartphone },
  { id: "googlepay", label: "Google Pay", icon: Smartphone },
  { id: "square", label: "Square", icon: CreditCard },
  { id: "cashapp", label: "CashApp", icon: Wallet },
  { id: "zelle", label: "Zelle", icon: Wallet },
];

const CITIES = ["Atlanta", "Peachtree City", "Fayetteville", "Alpharetta", "Marietta", "Riverdale", "Jonesboro", "McDonough", "Stockbridge"];

function tierEstimate(tier) {
  if (!tier || tier.quote_required) return 0;
  const low = tier.price_low || 0;
  const high = tier.price_high || Math.round(low * 1.3);
  return Math.round((low + high) / 2);
}

function tierRangeLabel(tier) {
  if (!tier) return "—";
  if (tier.quote_required) return "Quote required";
  if (tier.price_high && tier.price_high !== tier.price_low) return `$${tier.price_low} – $${tier.price_high}`;
  return `$${tier.price_low}+`;
}

function useQuery() { return new URLSearchParams(useLocation().search); }

export default function BookingPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const q = useQuery();
  const [services, setServices] = useState([]);
  const [addOns, setAddOns] = useState([]);
  const [membership, setMembership] = useState(null);
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    service_id: q.get("service") || "",
    property_size: "", // tier id
    date: "",
    time_slot: "",
    address: "",
    city: "Atlanta",
    notes: "",
    photos: [],
    add_ons: [],
    payment_method: "stripe",
    is_recurring: false,
    recurrence: "biweekly",
  });

  useEffect(() => {
    api.get("/services").then((r) => {
      const list = r.data.services || [];
      setServices(list);
      setAddOns(r.data.add_ons || []);
      if (!form.service_id && list.length) setForm((f) => ({ ...f, service_id: list[0].id }));
    });
    if (user) api.get("/membership/me").then(r => setMembership(r.data.plan)).catch(() => {});
  }, [user]);

  const service = services.find((s) => s.id === form.service_id);
  const tiers = service?.tiers || [];
  const selectedTier = tiers.find((t) => t.id === form.property_size);

  const subtotal = useMemo(() => tierEstimate(selectedTier), [selectedTier]);

  const addOnsTotal = useMemo(() =>
    form.add_ons.reduce((sum, id) => {
      const a = addOns.find((x) => x.id === id);
      if (!a) return sum;
      const high = a.price_high || a.price_low;
      return sum + Math.round((a.price_low + high) / 2);
    }, 0), [form.add_ons, addOns]
  );

  const recurringDiscountPct = form.is_recurring
    ? (form.recurrence === "weekly" ? 15 : form.recurrence === "biweekly" ? 10 : 0)
    : 0;
  const memberDiscountPct = membership?.discount_percent || 0;
  const effectiveDiscountPct = Math.max(recurringDiscountPct, memberDiscountPct);
  const discount = Math.round(subtotal * effectiveDiscountPct / 100);
  const total = Math.max(subtotal - discount, 0) + addOnsTotal;
  const quoteRequired = !!selectedTier?.quote_required;

  const onPhotos = async (files) => {
    const arr = await Promise.all(Array.from(files).slice(0, 4).map(f => new Promise((res) => {
      const r = new FileReader();
      r.onload = () => res(r.result);
      r.readAsDataURL(f);
    })));
    setForm((f) => ({ ...f, photos: [...f.photos, ...arr].slice(0, 4) }));
  };

  const removePhoto = (idx) => setForm((f) => ({ ...f, photos: f.photos.filter((_, i) => i !== idx) }));

  const toggleAddOn = (id) => setForm((f) => ({
    ...f,
    add_ons: f.add_ons.includes(id) ? f.add_ons.filter(x => x !== id) : [...f.add_ons, id],
  }));

  const validateStep = () => {
    if (step === 1 && (!form.service_id || !form.property_size)) { toast.error("Pick a service and tier"); return false; }
    if (step === 2 && (!form.date || !form.time_slot)) { toast.error("Pick a date and time"); return false; }
    if (step === 3 && (!form.address || !form.city)) { toast.error("Enter your address"); return false; }
    return true;
  };

  const next = () => { if (validateStep()) setStep((s) => Math.min(s + 1, 4)); };
  const back = () => setStep((s) => Math.max(s - 1, 1));

  const submit = async () => {
    if (quoteRequired) {
      toast.message("This tier requires a custom quote", { description: "Switching to AI Quote — upload a photo to get a price." });
      nav("/ai-quote");
      return;
    }
    if (!user) {
      toast.message("Sign in to confirm booking", { description: "Create a free account or log in to continue." });
      nav(`/login?next=/booking`);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        service_id: form.service_id,
        service_name: service?.name || "",
        property_size: form.property_size,
        date: form.date,
        time_slot: form.time_slot,
        address: form.address,
        city: form.city,
        notes: form.notes,
        photos: form.photos,
        estimated_price: total,
        payment_method: form.payment_method,
        is_recurring: form.is_recurring,
        recurrence: form.is_recurring ? form.recurrence : null,
      };
      await api.post("/bookings", payload);
      toast.success("Booking confirmed!", { description: "You'll get an email confirmation shortly." });
      nav("/dashboard");
    } catch (e) {
      toast.error("Could not create booking", { description: e?.response?.data?.detail || "Try again." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container-x py-16 md:py-24" data-testid="booking-page">
      <div className="max-w-3xl">
        <div className="overline mb-4">Online Booking</div>
        <h1 className="font-serif text-4xl md:text-5xl tracking-tight text-[#111]">Book your shine in 4 quick steps.</h1>
        {membership && membership.id !== "free" && (
          <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/40 text-[#A88926] text-xs font-semibold uppercase tracking-widest">
            <Check className="w-3 h-3" /> {membership.name} · {membership.discount_percent}% off applied
          </div>
        )}
      </div>

      <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          <div className="flex items-center gap-2 mb-10" data-testid="booking-progress">
            {[1,2,3,4].map((n) => (
              <div key={n} className="flex-1 flex items-center gap-2">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-medium ${
                  step >= n ? "bg-[#0B5D3B] text-white" : "bg-white border border-[#EAE3D2] text-[#7A8A81]"}`}>
                  {step > n ? <Check className="w-4 h-4" /> : n}
                </div>
                {n < 4 && <div className={`flex-1 h-0.5 ${step > n ? "bg-[#0B5D3B]" : "bg-[#EAE3D2]"}`} />}
              </div>
            ))}
          </div>

          {step === 1 && (
            <div className="space-y-8" data-testid="step-1">
              <div>
                <label className="overline mb-3 block">Service</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {services.map((s) => (
                    <button key={s.id} type="button"
                      onClick={() => setForm((f) => ({ ...f, service_id: s.id, property_size: "" }))}
                      className={`text-left p-5 rounded-2xl border transition-all ${
                        form.service_id === s.id ? "border-[#0B5D3B] bg-[#FAF7EE] shadow-md" : "border-[#EAE3D2] bg-white hover:border-[#0B5D3B]"
                      }`}
                      data-testid={`service-option-${s.id}`}>
                      <div className="flex items-baseline justify-between">
                        <div className="font-serif text-lg text-[#111]">{s.name}</div>
                        <div className="text-xs text-[#D4AF37] font-semibold">${s.price_from}+</div>
                      </div>
                      <div className="text-xs text-[#7A8A81] mt-1">{s.duration}</div>
                    </button>
                  ))}
                </div>
              </div>
              {tiers.length > 0 && (
                <div>
                  <label className="overline mb-3 block">{service?.name} — pick a tier</label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {tiers.map((t) => (
                      <button key={t.id} type="button"
                        onClick={() => setForm((f) => ({ ...f, property_size: t.id }))}
                        className={`text-left px-4 py-3.5 rounded-xl border text-sm flex items-center justify-between gap-3 transition-all ${
                          form.property_size === t.id ? "border-[#0B5D3B] bg-[#FAF7EE]" : "border-[#EAE3D2] hover:border-[#0B5D3B]"
                        }`}
                        data-testid={`tier-${t.id}`}>
                        <span className="text-[#111]">{t.label}</span>
                        <span className="font-semibold text-[#D4AF37] whitespace-nowrap">{tierRangeLabel(t)}</span>
                      </button>
                    ))}
                  </div>
                  {quoteRequired && (
                    <p className="mt-3 text-xs text-[#7A8A81]">This tier requires an on-site inspection or photo quote — we'll route you to the AI Quote tool.</p>
                  )}
                </div>
              )}
              <div className="flex items-center gap-3 p-5 rounded-2xl border border-[#EAE3D2]">
                <input type="checkbox" id="recurring" checked={form.is_recurring}
                  onChange={(e) => setForm((f) => ({ ...f, is_recurring: e.target.checked }))}
                  className="w-5 h-5 accent-[#0B5D3B]"
                  data-testid="recurring-checkbox" />
                <label htmlFor="recurring" className="text-sm flex-1">
                  <span className="font-medium">Make it recurring</span>
                  <span className="text-[#7A8A81]"> — weekly saves 15%, bi-weekly saves 10%</span>
                </label>
                {form.is_recurring && (
                  <select value={form.recurrence} onChange={(e) => setForm((f) => ({ ...f, recurrence: e.target.value }))}
                    className="px-3 py-2 rounded-full border border-[#EAE3D2] text-sm" data-testid="recurrence-select">
                    <option value="weekly">Weekly · 15% off</option>
                    <option value="biweekly">Bi-weekly · 10% off</option>
                    <option value="monthly">Monthly · standard</option>
                  </select>
                )}
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-8" data-testid="step-2">
              <div>
                <label className="overline mb-3 block">Date</label>
                <input type="date" value={form.date}
                  min={new Date().toISOString().split("T")[0]}
                  onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                  className="w-full md:w-72 px-5 py-3.5 rounded-full border border-[#EAE3D2] focus:border-[#0B5D3B] focus:outline-none"
                  data-testid="date-input" />
              </div>
              <div>
                <label className="overline mb-3 block">Time slot</label>
                <div className="flex flex-wrap gap-2">
                  {TIME_SLOTS.map((t) => (
                    <button key={t} type="button"
                      onClick={() => setForm((f) => ({ ...f, time_slot: t }))}
                      className={`px-5 py-2.5 rounded-full border text-sm ${
                        form.time_slot === t ? "border-[#0B5D3B] bg-[#0B5D3B] text-white" : "border-[#EAE3D2] hover:border-[#0B5D3B]"
                      }`}
                      data-testid={`time-${t.replace(/\s|:/g,'-')}`}>
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-7" data-testid="step-3">
              <div>
                <label className="overline mb-3 block">Address</label>
                <input value={form.address}
                  onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
                  placeholder="Street address"
                  className="w-full px-5 py-3.5 rounded-full border border-[#EAE3D2] focus:border-[#0B5D3B] focus:outline-none"
                  data-testid="address-input" />
              </div>
              <div>
                <label className="overline mb-3 block">City</label>
                <select value={form.city} onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
                  className="w-full md:w-72 px-5 py-3.5 rounded-full border border-[#EAE3D2]"
                  data-testid="city-select">
                  {CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="overline mb-3 block">Add-ons (optional)</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {addOns.map((a) => {
                    const checked = form.add_ons.includes(a.id);
                    const range = a.price_high && a.price_high !== a.price_low ? `$${a.price_low}–${a.price_high}` : `$${a.price_low}+`;
                    return (
                      <button key={a.id} type="button" onClick={() => toggleAddOn(a.id)}
                        className={`flex items-center gap-3 px-4 py-3 rounded-xl border text-sm transition-all ${
                          checked ? "border-[#0B5D3B] bg-[#FAF7EE]" : "border-[#EAE3D2] hover:border-[#0B5D3B]"
                        }`}
                        data-testid={`addon-${a.id}`}>
                        <span className={`w-5 h-5 rounded-md border-2 flex items-center justify-center ${
                          checked ? "bg-[#0B5D3B] border-[#0B5D3B] text-white" : "border-[#EAE3D2]"
                        }`}>{checked && <Check className="w-3 h-3" />}</span>
                        <span className="flex-1 text-left text-[#111]">{a.label}</span>
                        <span className="text-[#D4AF37] font-semibold">{range}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <label className="overline mb-3 block">Photos (optional)</label>
                <div className="flex flex-wrap gap-3">
                  {form.photos.map((p, i) => (
                    <div key={i} className="relative w-24 h-24 rounded-xl overflow-hidden border border-[#EAE3D2]">
                      <img src={p} alt="" className="w-full h-full object-cover" />
                      <button onClick={() => removePhoto(i)} type="button" className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center" data-testid={`remove-photo-${i}`}>
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  {form.photos.length < 4 && (
                    <label className="w-24 h-24 rounded-xl border-2 border-dashed border-[#EAE3D2] hover:border-[#0B5D3B] flex flex-col items-center justify-center cursor-pointer text-[#7A8A81]" data-testid="upload-photos-label">
                      <ImageIcon className="w-5 h-5 mb-1" />
                      <span className="text-[10px] uppercase tracking-widest">Add</span>
                      <input type="file" multiple accept="image/*" className="hidden"
                        onChange={(e) => onPhotos(e.target.files)}
                        data-testid="upload-photos-input" />
                    </label>
                  )}
                </div>
              </div>
              <div>
                <label className="overline mb-3 block">Notes</label>
                <textarea rows={3} value={form.notes}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                  placeholder="Pets, parking, special requests..."
                  className="w-full px-5 py-3 rounded-2xl border border-[#EAE3D2] focus:border-[#0B5D3B] focus:outline-none"
                  data-testid="notes-input" />
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-6" data-testid="step-4">
              <div>
                <label className="overline mb-3 block">Payment method</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {PAYMENTS.map((p) => {
                    const Icon = p.icon;
                    return (
                      <button key={p.id} type="button"
                        onClick={() => setForm((f) => ({ ...f, payment_method: p.id }))}
                        className={`flex items-center gap-3 px-5 py-4 rounded-2xl border text-sm ${
                          form.payment_method === p.id ? "border-[#0B5D3B] bg-[#FAF7EE]" : "border-[#EAE3D2] hover:border-[#0B5D3B]"
                        }`}
                        data-testid={`pay-${p.id}`}>
                        <Icon className="w-4 h-4 text-[#0B5D3B]" />
                        <span className="flex-1 text-left">{p.label}</span>
                        {form.payment_method === p.id && <Check className="w-4 h-4 text-[#0B5D3B]" />}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-[#7A8A81] mt-3">A 25% deposit will be charged to confirm your slot. The remainder is charged after service.</p>
              </div>
            </div>
          )}

          <div className="mt-12 flex justify-between">
            <button onClick={back} disabled={step === 1}
              className="text-sm text-[#7A8A81] hover:text-[#111] inline-flex items-center gap-2 disabled:opacity-40"
              data-testid="back-btn">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            {step < 4 ? (
              <button onClick={next} className="btn-primary" data-testid="next-btn">
                Continue <ArrowRight className="w-4 h-4 ml-2" />
              </button>
            ) : (
              <button onClick={submit} disabled={submitting} className="btn-primary disabled:opacity-60" data-testid="confirm-booking">
                {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Confirming…</> : (quoteRequired ? "Get custom quote" : "Confirm booking")}
              </button>
            )}
          </div>
        </div>

        <aside className="lg:sticky lg:top-28 self-start">
          <div className="card-luxe p-7" data-testid="booking-summary">
            <div className="overline mb-4">Estimate</div>
            {quoteRequired ? (
              <>
                <div className="font-serif text-3xl text-[#0B5D3B] mb-1">Quote required</div>
                <div className="text-xs text-[#7A8A81] mb-6">We'll inspect or analyze photos to give you an exact price.</div>
              </>
            ) : (
              <>
                <div className="font-serif text-5xl text-[#0B5D3B] mb-1">${total}</div>
                <div className="text-xs text-[#7A8A81] mb-6">
                  {effectiveDiscountPct > 0 ? `Includes ${effectiveDiscountPct}% discount · ` : ""}
                  Estimated total
                </div>
              </>
            )}
            <div className="border-t border-[#EAE3D2] pt-4 space-y-2.5 text-sm">
              <div className="flex justify-between"><span className="text-[#7A8A81]">Service</span><span className="text-[#111]">{service?.name || "—"}</span></div>
              <div className="flex justify-between"><span className="text-[#7A8A81]">Tier</span><span className="text-[#111] text-right">{selectedTier?.label || "—"}</span></div>
              <div className="flex justify-between"><span className="text-[#7A8A81]">Base estimate</span><span className="text-[#111]">{quoteRequired ? "—" : `$${subtotal}`}</span></div>
              {form.add_ons.length > 0 && <div className="flex justify-between"><span className="text-[#7A8A81]">Add-ons ({form.add_ons.length})</span><span className="text-[#111]">+${addOnsTotal}</span></div>}
              {effectiveDiscountPct > 0 && !quoteRequired && <div className="flex justify-between text-[#0B5D3B]"><span>Discount ({effectiveDiscountPct}%)</span><span>−${discount}</span></div>}
              <div className="flex justify-between"><span className="text-[#7A8A81]">Date</span><span className="text-[#111]">{form.date || "—"}</span></div>
              <div className="flex justify-between"><span className="text-[#7A8A81]">Time</span><span className="text-[#111]">{form.time_slot || "—"}</span></div>
              <div className="flex justify-between"><span className="text-[#7A8A81]">City</span><span className="text-[#111]">{form.city || "—"}</span></div>
            </div>
            <div className="mt-6 p-4 rounded-xl bg-[#FAF7EE] text-xs text-[#4A554E] flex gap-2">
              <CalIcon className="w-4 h-4 text-[#0B5D3B] shrink-0" />
              Free reschedule up to 24h before your appointment.
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
